import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { List, X, Phone, Sun, Moon, Globe, Check } from "@phosphor-icons/react";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { NAP } from "@/data/site";
import { track } from "@/lib/api";

const LINKS = [
  { to: "/services", key: "nav.services" },
  { to: "/digital", key: "nav.digital" },
  { to: "/about", key: "nav.about" },
  { to: "/contact", key: "nav.contact" },
];

function LangSwitcher({ compact = false }) {
  const { lang, setLang, langs, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const current = langs.find((l) => l.code === lang);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={t("lang.label")}
        data-testid="lang-switcher"
        className={`inline-flex items-center gap-2 border border-border text-foreground hover:border-primary hover:text-primary transition-colors ${
          compact ? "w-full justify-center h-11" : "h-9 px-3"
        }`}
      >
        <Globe size={16} />
        <span className="font-mono-tech text-[11px] tracking-widest uppercase">{current?.code}</span>
      </button>
      {open && (
        <div
          className={`absolute right-0 mt-2 w-44 bg-card border border-border z-50 ${compact ? "bottom-full mb-2 mt-0" : ""}`}
          data-testid="lang-menu"
        >
          {langs.map((l) => (
            <button
              key={l.code}
              onClick={() => {
                setLang(l.code);
                setOpen(false);
                track("lang_change", { lang: l.code });
              }}
              data-testid={`lang-option-${l.code}`}
              className={`w-full flex items-center justify-between px-4 h-10 font-mono-tech text-xs tracking-wide hover:bg-secondary/60 transition-colors ${
                lang === l.code ? "text-primary" : "text-foreground"
              }`}
            >
              {l.label}
              {lang === l.code && <Check size={14} />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { light, toggle } = useTheme();
  const { t } = useI18n();
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 border-b ${
        scrolled || open
          ? "bg-background/85 backdrop-blur-md border-border"
          : "bg-transparent border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="h-16 sm:h-20 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
            <img
              src="/mre-logo.webp"
              alt="MRE Developments logo"
              className="mre-logo h-10 w-10 object-contain"
            />
            <span className="font-display tracking-tight text-lg sm:text-xl uppercase">
              MRE<span className="text-muted-foreground">Developments</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `font-mono-tech text-xs tracking-widest uppercase transition-colors ${
                    isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {t(l.key)}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden sm:block">
              <LangSwitcher />
            </div>
            <button
              onClick={toggle}
              aria-label="Toggle theme"
              data-testid="theme-toggle"
              className="grid place-items-center w-9 h-9 border border-border text-foreground hover:border-primary hover:text-primary transition-colors"
            >
              {light ? <Moon size={16} /> : <Sun size={16} />}
            </button>
            <a
              href={`tel:${NAP.phoneHref}`}
              onClick={() => track("click_to_call", { location: "navbar" })}
              data-testid="nav-call"
              className="hidden sm:grid place-items-center w-9 h-9 border border-border text-foreground hover:border-primary hover:text-primary transition-colors"
              aria-label={`Call ${NAP.phoneDisplay}`}
            >
              <Phone size={16} />
            </a>
            <Link
              to="/contact"
              onClick={() => track("cta_click", { cta: "free_quote", location: "navbar" })}
              data-testid="nav-quote-cta"
              className="hidden sm:inline-flex items-center bg-primary text-primary-foreground font-mono-tech text-xs tracking-widest uppercase px-4 h-9 hover:bg-[hsl(16,100%,55%)] transition-colors"
            >
              {t("act.freeQuote")}
            </Link>
            <button
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden grid place-items-center w-9 h-9 border border-border"
              aria-label="Menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X size={18} /> : <List size={18} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background" data-testid="mobile-menu">
          <div className="px-5 py-6 flex flex-col gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className="font-display uppercase tracking-tight text-2xl py-2 border-b border-border/60"
                data-testid={`mobile-link-${l.to.replace("/", "")}`}
              >
                {t(l.key)}
              </NavLink>
            ))}
            <div className="mt-5">
              <LangSwitcher compact />
            </div>
            <div className="flex gap-3 mt-3">
              <a
                href={`tel:${NAP.phoneHref}`}
                className="flex-1 inline-flex items-center justify-center gap-2 border border-border h-11 font-mono-tech text-xs tracking-widest uppercase"
                data-testid="mobile-call"
              >
                <Phone size={15} /> {t("act.call")}
              </a>
              <Link
                to="/contact"
                className="flex-1 inline-flex items-center justify-center bg-primary text-primary-foreground h-11 font-mono-tech text-xs tracking-widest uppercase"
                data-testid="mobile-quote"
              >
                {t("act.freeQuote")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
